import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { memo, useState } from "react";

export const advancedTrackerSettingsForm = groupInput({
  alwaysPreserveCarrierInTrackerSyncs: input<boolean>(),
  keepRetryingTrackerSyncsForAtLeastHours: input<number | null>(),
});

export type AdvancedTrackerSettingsForm = InputSchema.Input<
  typeof advancedTrackerSettingsForm
>;

export type AdvancedTrackerSettingsValue = InputSchema.Value<
  typeof advancedTrackerSettingsForm
>;

export const advancedTrackerSettingsDefault: AdvancedTrackerSettingsValue = {
  alwaysPreserveCarrierInTrackerSyncs: false,
  keepRetryingTrackerSyncsForAtLeastHours: null,
};

export const AdvancedTrackerSettingsCard = memo(
  function AdvancedTrackerSettingsCard({
    input,
  }: {
    input: AdvancedTrackerSettingsForm;
  }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const {
      alwaysPreserveCarrierInTrackerSyncs,
      keepRetryingTrackerSyncsForAtLeastHours,
    } = input.inputs;

    return (
      <Card title="Advanced Tracker Settings">
        {!isExpanded ? (
          <Flex
            align="center"
            dir="row"
            justify="space-between"
            onClick={() => setIsExpanded(true)}
            style={{ cursor: "pointer" }}
          >
            <Text fontSize="sm" textColor="tertiary">
              Rarely used settings for carrier compatibility issues
            </Text>
            <Text fontSize="sm" textColor="tertiary">
              Click to expand ▼
            </Text>
          </Flex>
        ) : (
          <Flex dir="column" gap="md">
            <Flex align="center" dir="row" justify="space-between">
              <Text fontSize="sm" textColor="tertiary">
                Settings for specific carrier compatibility issues
              </Text>
              <Text
                fontSize="sm"
                onClick={() => setIsExpanded(false)}
                style={{ cursor: "pointer" }}
                textColor="tertiary"
              >
                Collapse ▲
              </Text>
            </Flex>

            <Flex dir="column" gap="md">
              <Flex dir="column" gap="sm">
                <Flex align="center" dir="row" gap="md">
                  <RedoToggle
                    setValue={(value) =>
                      alwaysPreserveCarrierInTrackerSyncs.setValue(value)
                    }
                    value={alwaysPreserveCarrierInTrackerSyncs.value}
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    Always preserve carrier in tracker syncs
                  </Text>
                </Flex>
                <Text fontSize="xs" textColor="tertiary">
                  When enabled, prevents our system from removing or modifying
                  the original carrier information in EasyPost tracker sync
                  requests. This helps with carriers that require exact carrier
                  matching (e.g., Mail Innovations), but may reduce tracker sync
                  success rates for misidentified carriers.
                </Text>
              </Flex>

              <Flex dir="column" gap="sm">
                <Flex align="center" dir="row" gap="md">
                  <Text fontSize="sm" fontWeight="medium">
                    Keep retrying tracker syncs for at least this many hours:
                  </Text>
                  <RedoIncrementDecrement
                    max={168}
                    min={0}
                    setValue={(value) =>
                      keepRetryingTrackerSyncsForAtLeastHours.setValue(
                        value === 0 ? null : value,
                      )
                    }
                    size="small"
                    value={keepRetryingTrackerSyncsForAtLeastHours.value || 0}
                  />
                </Flex>
                <Text fontSize="xs" textColor="tertiary">
                  Continue retrying tracker syncs regardless of the normal
                  3-attempt limit for fulfillments created within this many
                  hours. Set to 0 to disable. Useful for carriers like Mail
                  Innovations where trackers become valid after delays. Note:
                  When this setting is enabled, the admin dashboard tracker sync
                  report field <code>"Orders not fully synced"</code> will be
                  inflated. Normally, that field only includes orders we tried
                  to sync on the most recent attempt, but this setting will
                  increase that count to include all orders created within the
                  syncing time window (default 30 days). If this is problematic,
                  ask an engineer to fix the performance-related issue in{" "}
                  <code>sync-team-trackers.ts</code>.
                </Text>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Card>
    );
  },
);
